/**
 * ActivityLogTab.jsx - Full Activity Log Table Component
 * 
 * Checklist Items Covered:
 * - H1: Activity Filter Bar
 * - H2: Channel Filter (Email/SMS)
 * - H3: Status Filter
 * - H4: Date Range Filter
 * - H5: Activity Table Header
 * - H6: Date Column
 * - H7: Guest Column
 * - H8: Channel Column
 * - H9: Template Column
 * - H10: Status Column
 * - H11: Open/Click Column
 * - H12: Activity Row
 * - H13: Pagination
 * - H14: Export Button
 */

import React, { useState, useMemo } from 'react';
import { 
  Mail, MessageSquare, Filter, Calendar, Download, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Clock, MailOpen, MousePointer, Eye, Search
} from 'lucide-react';
import { COLORS } from './messagingStyles';

// ─── Constants ──────────────────────────────────────────────────────────────────

const ITEMS_PER_PAGE = 15;

const CHANNEL_OPTIONS = [
  { key: 'all', label: 'All Channels' },
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'SMS' },
];

const STATUS_OPTIONS = [
  { key: 'all', label: 'All Status' },
  { key: 'sent', label: 'Sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'opened', label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'failed', label: 'Failed' },
];

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = {
  container: {
    padding: 24,
    minHeight: 'calc(100vh - 200px)',
  },
  
  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  headerSubtitle: {
    fontSize: 13,
    color: COLORS.textMuted,
    marginTop: 4,
  },
  
  // H1: Filter Bar
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 20px',
    borderRadius: 14,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    marginBottom: 20,
  },
  filterIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 10,
    background: COLORS.goldLight,
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.goldDark,
  },
  filterDivider: {
    width: 1,
    height: 24,
    background: COLORS.borderMedium,
  },
  
  // H2-H4: Filter Dropdowns
  filterDropdown: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  filterDropdownActive: {
    borderColor: COLORS.gold,
    background: COLORS.goldLight,
  },
  filterDropdownText: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textSecondary,
  },
  
  // Search
  searchWrapper: {
    flex: 1,
    position: 'relative',
  },
  searchInput: {
    width: '100%',
    padding: '10px 14px 10px 36px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.inputBg,
    fontSize: 13,
    color: COLORS.textPrimary,
    outline: 'none',
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  },
  
  // H14: Export Button
  exportBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.goldDark,
  },
  
  // Table Container
  tableContainer: {
    borderRadius: 16,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    overflow: 'hidden',
  },
  
  // H5: Table Header
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr 100px 180px 100px 120px',
    gap: 12,
    padding: '14px 20px',
    background: '#FFFCF7',
    borderBottom: `1px solid ${COLORS.borderMedium}`,
  },
  tableHeaderCell: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  
  // H12: Table Row
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr 100px 180px 100px 120px',
    gap: 12,
    padding: '14px 20px',
    borderBottom: `1px solid ${COLORS.borderLight}`,
    alignItems: 'center',
    transition: 'background 0.15s',
  },
  tableRowLast: {
    borderBottom: 'none',
  },
  tableCell: {
    fontSize: 13,
    color: COLORS.textPrimary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tableCellMuted: {
    color: COLORS.textMuted,
    fontSize: 12,
  },
  
  // H8: Channel Badge
  channelBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  channelEmail: {
    background: 'rgba(198,167,90,0.15)',
    color: COLORS.goldDark,
  },
  channelSms: {
    background: 'rgba(59,130,246,0.15)',
    color: '#1d4ed8',
  },
  
  // H10: Status Badge
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
  },
  statusSent: {
    background: 'rgba(107,114,128,0.15)',
    color: '#4b5563',
  },
  statusDelivered: {
    background: 'rgba(74,140,90,0.15)',
    color: COLORS.success,
  },
  statusOpened: {
    background: 'rgba(198,167,90,0.15)',
    color: COLORS.goldDark,
  },
  statusClicked: {
    background: 'rgba(59,130,246,0.15)',
    color: '#1d4ed8',
  },
  statusFailed: {
    background: 'rgba(220,38,38,0.15)',
    color: COLORS.error,
  },
  
  // H11: Engagement Icons
  engagementCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  engagementIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 8,
    background: '#F5F5F5',
    position: 'relative',
    cursor: 'help',
  },
  engagementIconActive: {
    background: COLORS.goldLight,
  },
  engagementLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: COLORS.textSecondary,
    position: 'absolute',
    bottom: -14,
    left: '50%',
    transform: 'translateX(-50%)',
    whiteSpace: 'nowrap',
  },
  
  // Empty State
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginTop: 16,
    marginBottom: 8,
  },
  emptyBody: {
    fontSize: 13,
    color: COLORS.textMuted,
    maxWidth: 300,
    lineHeight: 1.5,
  },
  
  // H13: Pagination
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderTop: `1px solid ${COLORS.borderLight}`,
  },
  paginationInfo: {
    fontSize: 12,
    color: COLORS.textMuted,
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  paginationBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 8,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  paginationBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  paginationPage: {
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textSecondary,
  },
};

// ─── Helper Functions ───────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const getStatusStyle = (status) => {
  switch (status?.toLowerCase()) {
    case 'clicked': return styles.statusClicked;
    case 'opened': return styles.statusOpened;
    case 'delivered': return styles.statusDelivered;
    case 'failed': return styles.statusFailed;
    default: return styles.statusSent;
  }
};

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'clicked': return MousePointer;
    case 'opened': return MailOpen;
    case 'delivered': return CheckCircle;
    case 'failed': return XCircle;
    default: return Clock;
  }
};

// ─── Component ──────────────────────────────────────────────────────────────────

const ActivityLogTab = ({
  emailLogs = [],
  smsLogs = [],
  guests = [],
}) => {
  // State
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Build a lookup map from email -> guest name for fallback resolution
  const guestEmailMap = useMemo(() => {
    const map = new Map();
    guests.forEach(guest => {
      if (guest.email) {
        const name = [guest.first_name, guest.last_name].filter(Boolean).join(' ').trim();
        if (name) {
          map.set(guest.email.toLowerCase(), name);
        }
      }
    });
    return map;
  }, [guests]);

  // Helper to resolve guest name with fallback lookup
  const resolveGuestName = (log, channel) => {
    // Priority 1: Use guest_name or recipient_name from log if available
    const logName = log.guest_name || log.recipient_name;
    if (logName && logName.trim()) {
      return logName.trim();
    }
    
    // Priority 2: Lookup by email in guests array
    const logEmail = log.guest_email || log.email;
    if (channel === 'email' && logEmail && guestEmailMap.has(logEmail.toLowerCase())) {
      return guestEmailMap.get(logEmail.toLowerCase());
    }
    
    // Priority 3: Show the contact info (email/phone) as fallback
    const contactField = channel === 'email' ? (log.guest_email || log.email) : log.phone;
    if (contactField) {
      return contactField;
    }
    
    // Priority 4: Unknown
    return 'Unknown';
  };

  // Combine and process logs
  const allLogs = useMemo(() => {
    const emailEntries = emailLogs.map(log => ({
      id: log.id || log._id,
      date: log.sent_at || log.created_at,
      guest: resolveGuestName(log, 'email'),
      email: log.guest_email || log.email,
      channel: 'email',
      template: log.template_name || 'Template',
      status: log.status || 'Sent',
      opened: log.status === 'Opened' || log.status === 'Clicked' || !!log.opened_at,
      clicked: log.status === 'Clicked',
    }));
    
    const smsEntries = smsLogs.map(log => ({
      id: log.id || log._id,
      date: log.sent_at || log.created_at,
      guest: resolveGuestName(log, 'sms'),
      phone: log.phone,
      channel: 'sms',
      template: log.template_name || 'SMS',
      status: log.status || 'Sent',
      opened: false,
      clicked: false,
    }));
    
    return [...emailEntries, ...smsEntries].sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    );
  }, [emailLogs, smsLogs, guestEmailMap]);

  // Apply filters
  const filteredLogs = useMemo(() => {
    return allLogs.filter(log => {
      // Channel filter
      if (channelFilter !== 'all' && log.channel !== channelFilter) return false;
      
      // Status filter
      if (statusFilter !== 'all' && log.status?.toLowerCase() !== statusFilter) return false;
      
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesGuest = log.guest?.toLowerCase().includes(query);
        const matchesEmail = log.email?.toLowerCase().includes(query);
        const matchesTemplate = log.template?.toLowerCase().includes(query);
        if (!matchesGuest && !matchesEmail && !matchesTemplate) return false;
      }
      
      return true;
    });
  }, [allLogs, channelFilter, statusFilter, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE);
  const paginatedLogs = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredLogs.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredLogs, currentPage]);

  const handleExport = () => {
    // Convert to CSV
    const headers = ['Date', 'Guest', 'Channel', 'Template', 'Status', 'Opened', 'Clicked'];
    const rows = filteredLogs.map(log => [
      formatDate(log.date),
      log.guest,
      log.channel.toUpperCase(),
      log.template,
      log.status,
      log.opened ? 'Yes' : 'No',
      log.clicked ? 'Yes' : 'No',
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={styles.container} data-testid="activity-log-tab">
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.headerTitle}>Activity Log</div>
          <div style={styles.headerSubtitle}>
            Unified delivery history for email and SMS communication activity
          </div>
        </div>
      </div>

      {/* H1: Filter Bar */}
      <div style={styles.filterBar} data-testid="activity-filter-bar">
        <div style={styles.filterIcon}>
          <Filter size={14} color={COLORS.goldDark} />
          <span style={styles.filterLabel}>Filters</span>
        </div>
        <div style={styles.filterDivider} />
        
        {/* H2: Channel Filter */}
        <select
          value={channelFilter}
          onChange={(e) => { setChannelFilter(e.target.value); setCurrentPage(1); }}
          style={styles.filterDropdown}
          data-testid="channel-filter"
        >
          {CHANNEL_OPTIONS.map(opt => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>
        
        {/* H3: Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          style={styles.filterDropdown}
          data-testid="status-filter"
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>
        
        {/* H4: Date Range (simplified to search for now) */}
        <div style={styles.searchWrapper}>
          <Search size={14} color={COLORS.textMuted} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by guest, email, or template..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            style={styles.searchInput}
            data-testid="activity-search"
          />
        </div>
        
        {/* H14: Export Button */}
        <button
          style={styles.exportBtn}
          onClick={handleExport}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = COLORS.goldLight;
            e.currentTarget.style.borderColor = COLORS.gold;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = COLORS.surface;
            e.currentTarget.style.borderColor = COLORS.borderMedium;
          }}
          data-testid="export-btn"
        >
          <Download size={14} />
          Export
        </button>
      </div>

      {/* Table */}
      <div style={styles.tableContainer}>
        {/* H5: Table Header */}
        <div style={styles.tableHeader} data-testid="activity-table-header">
          <div style={styles.tableHeaderCell}>Date</div>
          <div style={styles.tableHeaderCell}>Guest</div>
          <div style={styles.tableHeaderCell}>Channel</div>
          <div style={styles.tableHeaderCell}>Template</div>
          <div style={styles.tableHeaderCell}>Status</div>
          <div style={styles.tableHeaderCell}>Engagement</div>
        </div>

        {/* H12: Table Rows */}
        {paginatedLogs.length === 0 ? (
          <div style={styles.emptyState}>
            <Mail size={40} color={COLORS.gold} />
            <div style={styles.emptyTitle}>No activity found</div>
            <div style={styles.emptyBody}>
              {searchQuery || channelFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your filters to see more results.'
                : 'Activity will appear here once you start sending messages.'}
            </div>
          </div>
        ) : (
          paginatedLogs.map((log, i) => {
            const StatusIcon = getStatusIcon(log.status);
            return (
              <div
                key={log.id}
                style={{
                  ...styles.tableRow,
                  ...(i === paginatedLogs.length - 1 ? styles.tableRowLast : {}),
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#FFFCF7'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                data-testid={`activity-row-${log.id}`}
              >
                {/* H6: Date Column */}
                <div style={styles.tableCell}>{formatDate(log.date)}</div>
                
                {/* H7: Guest Column */}
                <div>
                  <div style={styles.tableCell}>{log.guest}</div>
                  <div style={styles.tableCellMuted}>{log.email || log.phone}</div>
                </div>
                
                {/* H8: Channel Column */}
                <div>
                  <span style={{
                    ...styles.channelBadge,
                    ...(log.channel === 'email' ? styles.channelEmail : styles.channelSms),
                  }}>
                    {log.channel === 'email' ? <Mail size={10} /> : <MessageSquare size={10} />}
                    {log.channel.toUpperCase()}
                  </span>
                </div>
                
                {/* H9: Template Column */}
                <div style={styles.tableCell}>{log.template}</div>
                
                {/* H10: Status Column */}
                <div>
                  <span style={{ ...styles.statusBadge, ...getStatusStyle(log.status) }}>
                    <StatusIcon size={10} />
                    {log.status}
                  </span>
                </div>
                
                {/* H11: Open/Click Column */}
                <div style={styles.engagementCell}>
                  <div style={{
                    ...styles.engagementIcon,
                    ...(log.opened ? styles.engagementIconActive : {}),
                  }} title="Email was opened by recipient">
                    <Eye size={16} color={log.opened ? COLORS.goldDark : '#AAA'} />
                    <span style={styles.engagementLabel}>{log.opened ? 'Opened' : 'Not opened'}</span>
                  </div>
                  <div style={{
                    ...styles.engagementIcon,
                    ...(log.clicked ? styles.engagementIconActive : {}),
                  }} title="Link was clicked in email">
                    <MousePointer size={16} color={log.clicked ? COLORS.goldDark : '#AAA'} />
                    <span style={styles.engagementLabel}>{log.clicked ? 'Clicked' : 'No clicks'}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* H13: Pagination */}
        {filteredLogs.length > ITEMS_PER_PAGE && (
          <div style={styles.pagination} data-testid="activity-pagination">
            <div style={styles.paginationInfo}>
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
              {Math.min(currentPage * ITEMS_PER_PAGE, filteredLogs.length)} of {filteredLogs.length}
            </div>
            <div style={styles.paginationControls}>
              <button
                style={{
                  ...styles.paginationBtn,
                  ...(currentPage === 1 ? styles.paginationBtnDisabled : {}),
                }}
                onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                data-testid="pagination-prev"
              >
                <ChevronLeft size={16} color={COLORS.textSecondary} />
              </button>
              <span style={styles.paginationPage}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                style={{
                  ...styles.paginationBtn,
                  ...(currentPage === totalPages ? styles.paginationBtnDisabled : {}),
                }}
                onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                data-testid="pagination-next"
              >
                <ChevronRight size={16} color={COLORS.textSecondary} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogTab;
